import { useState } from "react";
import { Loader2, Mail, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLocale } from "@/i18n/context";
import { cn } from "@/lib/utils";
import { getEdgeFunctionErrorMessage } from "@/lib/edgeFunctionError";
import { subscribeToNewsletter } from "@/lib/newsletter";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Newsletter opt-in box. Rendered in the footer on every page; the status lives
 * in local state so a subscriber sees the confirmation without a navigation.
 */
export const NewsletterSignup = ({ className }: { className?: string }) => {
  const { t } = useLocale();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done">("idle");
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();

    if (!EMAIL_PATTERN.test(trimmed)) {
      setError(t.newsletter.invalidEmail);
      return;
    }

    setStatus("loading");
    setError("");

    try {
      await subscribeToNewsletter({ source: "footer", email: trimmed });
      setStatus("done");
      setEmail("");
    } catch (err) {
      setStatus("idle");
      setError(await getEdgeFunctionErrorMessage(err, t.newsletter.error));
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-5 md:flex-row md:items-center md:justify-center md:gap-10",
        className,
      )}
    >
      <div className="max-w-md">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold text-foreground">{t.newsletter.heading}</p>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {t.newsletter.description}
        </p>
      </div>

      <div className="w-full md:max-w-md">
        {status === "done" ? (
          <p className="flex items-center gap-2 text-sm font-medium text-primary">
            <Check className="h-4 w-4" />
            {t.newsletter.success}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-2" noValidate>
            <Label htmlFor="newsletter-email" className="sr-only">
              {t.newsletter.heading}
            </Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError("");
                }}
                placeholder={t.newsletter.placeholder}
                autoComplete="email"
                required
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "newsletter-error" : undefined}
                className={cn("bg-background", error && "border-destructive")}
              />
              <Button type="submit" disabled={status === "loading"} className="shrink-0">
                {status === "loading" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {status === "loading" ? t.common.sending : t.newsletter.submit}
              </Button>
            </div>
            {error ? (
              <p id="newsletter-error" role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">{t.newsletter.privacy}</p>
            )}
          </form>
        )}
      </div>
    </div>
  );
};
